import React, { useEffect, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import ProfilePicture from '../components/ProfilePicture';
import { SecurityContext } from '../utils/SecurityContext';

const vibrantColors = [
  "#FF5733", // Vivid Red
  "#FF8D1A", // Vivid Orange
  "#FFC300", // Vivid Yellow
  "#DAF7A6", // Vivid Light Green
  "#28B463", // Vivid Green
  "#1F618D", // Vivid Blue
  "#8E44AD", // Vivid Purple
  "#FF33F6", // Vivid Pink
  "#FF33FF", // Vivid Magenta
  "#33FFD1"  // Vivid Cyan
];

const Profile = ({ user, isConnected }) => {
  const navigate = useNavigate();
  const { setIsAuthenticated, setUser } = useContext(SecurityContext);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const getBgColor = () => {
    return vibrantColors[Math.floor(Math.random() * vibrantColors.length)];
  };

  // Fonction de déconnexion sécurisée
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      
      // Supprimer toutes les données de session
      const keysToRemove = [
        'authToken',
        'userData',
        'userSession',
        'refreshToken',
        'userPreferences',
        'sessionId',
        'user',
        'logged',
        'lang',
        'speed',
        'csrf_token'
      ];

      // Nettoyage localStorage et sessionStorage
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      // Nettoyage des cookies d'authentification
      document.cookie.split(";").forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (name.includes('auth') || name.includes('session') || name.includes('token')) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });

      // Réinitialiser le contexte de sécurité
      if (setIsAuthenticated) setIsAuthenticated(false);
      if (setUser) setUser(null);

      // Fermer le modal
      setShowLogoutModal(false);

      // Redirection sécurisée
      navigate('/login', { replace: true });

    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // En cas d'erreur, forcer la redirection
      navigate('/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Composant Modal de confirmation
  const LogoutModal = () => {
    // Hook pour la gestion des touches
    useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key === 'Escape' && !isLoggingOut && showLogoutModal) {
          setShowLogoutModal(false);
        }
      };

      if (showLogoutModal) {
        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }, [showLogoutModal, isLoggingOut]);

    // Retour conditionnel après les hooks
    if (!showLogoutModal) return null;

    const handleOverlayClick = (e) => {
      if (e.target === e.currentTarget && !isLoggingOut) {
        setShowLogoutModal(false);
      }
    };

    return (
      <div 
        className="logout-modal-overlay" 
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-title"
        aria-describedby="logout-description"
      >
        <div className="logout-modal-content">
          <h3 id="logout-title">
            Confirmation de déconnexion
          </h3>
          <p id="logout-description">
            Êtes-vous sûr de vouloir vous déconnecter ?
          </p>
          
          <div className="logout-modal-actions">
            <button 
              className="btn-cancel"
              onClick={() => setShowLogoutModal(false)}
              disabled={isLoggingOut}
              type="button"
              aria-label="Annuler"
            >
              Annuler
            </button>
            <button 
              className="btn-logout-confirm"
              onClick={handleLogout}
              disabled={isLoggingOut}
              type="button"
              aria-label={isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}
            >
              {isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    isConnected();

    if (!document.body.classList.contains("fingers"))
      document.body.classList.add("fingers");
  }, [isConnected]);

  return (
    <>
      <Header />
      <main className="profile-component">
        <section>
          <Link to="/account" className='edit-profile'>
            <ProfilePicture user={user} />
            <div>
              <svg width="139" height="139" viewBox="0 0 139 139" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M27.8781 34.767C27.8781 25.5462 31.5495 16.7031 38.0847 10.183C44.6199 3.66294 53.4836 0 62.7257 0C71.9679 0 80.8315 3.66294 87.3667 10.183C93.9019 16.7031 97.5734 25.5462 97.5734 34.767C97.5734 43.9878 93.9019 52.8309 87.3667 59.351C80.8315 65.8711 71.9679 69.534 62.7257 69.534C53.4836 69.534 44.6199 65.8711 38.0847 59.351C31.5495 52.8309 27.8781 43.9878 27.8781 34.767ZM62.7257 13.9068C59.98 13.9068 57.2611 14.4464 54.7244 15.4947C52.1876 16.543 49.8827 18.0796 47.9411 20.0166C45.9996 21.9537 44.4595 24.2533 43.4087 26.7841C42.358 29.315 41.8172 32.0276 41.8172 34.767C41.8172 37.5064 42.358 40.219 43.4087 42.7499C44.4595 45.2807 45.9996 47.5803 47.9411 49.5174C49.8827 51.4544 52.1876 52.991 54.7244 54.0393C57.2611 55.0876 59.98 55.6272 62.7257 55.6272C68.271 55.6272 73.5892 53.4294 77.5103 49.5174C81.4315 45.6053 83.6343 40.2995 83.6343 34.767C83.6343 29.2345 81.4315 23.9287 77.5103 20.0166C73.5892 16.1046 68.271 13.9068 62.7257 13.9068ZM16.8175 108.48C14.5663 111.331 13.9391 113.486 13.9391 114.731C13.9391 115.552 14.183 116.344 15.3678 117.401C16.7408 118.625 19.201 119.974 23.104 121.177C30.9099 123.583 42.312 124.8 55.9026 125.092C56.8178 125.111 57.7203 125.31 58.5586 125.677C59.3968 126.044 60.1543 126.572 60.7879 127.232C61.4215 127.891 61.9187 128.668 62.2512 129.519C62.5837 130.37 62.745 131.278 62.7257 132.191C62.7065 133.104 62.5072 134.005 62.1392 134.841C61.7712 135.677 61.2417 136.433 60.5809 137.065C59.9202 137.697 59.1411 138.193 58.2881 138.525C57.4352 138.857 56.5251 139.018 55.6099 138.998C41.6917 138.699 28.7075 137.462 18.992 134.465C14.1412 132.963 9.55522 130.87 6.0844 127.776C2.41842 124.508 0 120.113 0 114.731C0 109.259 2.49509 104.141 5.88228 99.8578C9.32523 95.5119 14.0854 91.5137 19.668 88.1065C30.8402 81.32 46.0337 76.4874 62.7257 76.4874C65.8411 76.4874 68.9077 76.6543 71.8976 76.9741C73.7359 77.1687 75.4214 78.0838 76.5834 79.5183C77.7453 80.9527 78.2886 82.7889 78.0935 84.6229C77.8985 86.4569 76.9813 88.1385 75.5435 89.2978C74.1058 90.457 72.2653 90.999 70.4271 90.8044C67.918 90.5333 65.3393 90.3942 62.7257 90.3942C48.6264 90.3942 35.9418 94.4967 26.9303 99.9829C22.421 102.723 19.0129 105.699 16.8175 108.48ZM133.906 83.6772C132.288 82.0625 130.367 80.7817 128.252 79.9078C126.138 79.0339 123.872 78.5841 121.583 78.5841C119.295 78.5841 117.029 79.0339 114.915 79.9078C112.8 80.7817 110.879 82.0625 109.261 83.6772L85.4394 107.437C83.84 109.034 82.8031 111.105 82.4843 113.34L80.1844 129.389C80.0305 130.459 80.1287 131.551 80.4711 132.576C80.8135 133.602 81.3907 134.534 82.1571 135.299C82.9234 136.064 83.8577 136.639 84.886 136.981C85.9142 137.323 87.0081 137.421 88.0809 137.267L104.167 134.972C106.407 134.654 108.483 133.620 110.084 132.024L133.899 108.257C137.165 104.998 139 100.577 139 95.9673C139 91.3579 137.172 86.9371 133.906 83.6772ZM119.116 93.5093C119.773 92.876 120.654 92.5256 121.567 92.5335C122.481 92.5414 123.355 92.9071 124.001 93.5517C124.647 94.1963 125.014 95.0683 125.022 95.9799C125.03 96.8914 124.678 97.7697 124.044 98.4254L101.044 121.372L95.2943 122.192L96.1167 116.456L119.116 93.5093Z" fill="white" />
              </svg>
            </div>
          </Link>
          <h1 className='name_user'>{user.prenom}</h1>
        </section>
        
        <section>
          <ul>
            <li>
              <Link to="/achievements">
                <span>Mes récompenses</span> 
                <img src='./assets/images/achievements.webp' alt="Médaille" />
              </Link>
            </li>
            <li>
              <Link to="/ranking">
                <span>Mon classement</span>
                <img src='./assets/images/ranking.webp' alt="Podium" />
              </Link>
            </li>
            <li>
              <Link to="/pot">
                <span>Ma cagnotte</span>
                <img src='./assets/images/pot.webp' alt="Pig" />
              </Link>
            </li>
            <li>
              <Link to="/history">
                <span>Mes épisodes</span>
                <img src='./assets/images/cadenas.webp' alt="Unlock" />
              </Link>
            </li>
          </ul>
        </section>

        {/* Section bouton de déconnexion */}
        <section className="logout-section">
          <button 
            className="logout-btn"
            onClick={() => setShowLogoutModal(true)}
            type="button"
            aria-label="Se déconnecter"
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path 
                d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9M16 17L21 12M21 12L16 7M21 12H9" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <span>Déconnexion</span>
          </button>
        </section>
      </main>
      
      <Footer visible={true} active={"profile"} />
      <LogoutModal />
    </>
  );
};

export default Profile;